import axios, { AxiosError, AxiosResponse, Method } from 'axios';

import common from '@/translations/fr/common';
import projectUsers from '@/translations/fr/components/project/projectUsers';
import notificationManager from '@/services/Notification/NotificationManager';
import loginService from '@/services/Security/loginService';
import { getCookie, setCookie } from '@cnamts/accessmaster-jwt/utils';
import router from '@/config/router/router';
import dataTable from '@/translations/fr/components/globals/dataTable';

export enum METHODS {
	GET = 'GET',
	HEAD = 'HEAD'
}

/** The axios instance */
const instance = axios.create({
	withCredentials: false,
	baseURL: window.API_URL,
	headers: {
		'Accept': 'application/json',
		'Content-Type': 'application/json'
	}
});

/** Default error message */
const DEFAULT_ERROR_MESSAGE = common.defaultErrorMessage;

// Response interceptor to handle errors globally
instance.interceptors.response.use(undefined, (error: AxiosError) => {
	let errorMessage = '';

	// If we don't have a error or a 500 HTTP Code
	if (!error.response || error.response.status >= 500) {
		const isHomeIndicatorsException = 500 === error.response?.status
			&& error.response?.data?.type.includes('HomeIndicatorsException');

		const isOutOfRangeCurrentPage = 500 === error.response?.status && error.response?.data?.type.includes('OutOfRangeCurrentPageException');

		switch (true) {
			case isHomeIndicatorsException:
				errorMessage = '';
				break;
			case isOutOfRangeCurrentPage:
				errorMessage = dataTable.errors.outOfRangePage;
				break;
			default:
				errorMessage = DEFAULT_ERROR_MESSAGE;
		}
	} else {
		const method = error.config.method || '';

		// Hide error notification when it's an expected error
		const is404 = method.toUpperCase() === METHODS.HEAD && 404 === error.response.status;
		const is400 = 400 === error.response.status;
		const is409Conflict = 409 === error.response.status && error.response.data?.type.includes('ProjectUserConflictException');
		const isExpiredToken = 401 === error.response.status && 'Expired JWT Token' === error.response.data.message;
		const isTermConditionInvalid = 401 === error.response.status && 'User term condition is invalid.' === error.response.data.message;
		const isPatientRedirectToSurvey = 401 === error.response.status && 'Votre token n\'est pas valide.' === error.response.data.message;

		const hideNotification = is404 || is400 || isExpiredToken || isTermConditionInvalid || isPatientRedirectToSurvey;

		if (is409Conflict) {
			error.response.data.message = ('' !== projectUsers.conflictExceptionMessage) ?
				projectUsers.conflictExceptionMessage : error.response.data.message;
		}

		if (false === hideNotification) {
			// Else, use message from server or fallback to default one
			errorMessage = error.response.data.message || DEFAULT_ERROR_MESSAGE;
		}

		if (isExpiredToken && getCookie('accessmasterTokenCookie')) {
			setCookie('accessmasterTokenCookie', '', -1, false);
			router.push({ path: 'accessmaster' });
			location.reload();
		}

		if (isExpiredToken && loginService.isLogged()) {
			loginService.logout();
			location.reload();
		}
	}

	if (errorMessage) {
		notificationManager.displayMessage('error', errorMessage);
	}

	return Promise.reject(error);
});

export { instance as axios };
export { AxiosError };
export { AxiosResponse };

export { Method };
